/**
 * Scope a calendar token is created with. SMEP's ics endpoint checks for exactly this, and nothing
 * else in the platform accepts it - so a leaked link reads event calendars and no other API.
 *
 * It does not narrow *which* calendars: identity scopes are free-form strings and SMEP only ever asks
 * whether this one is present, so the token reaches every event calendar its owner can read.
 */
export const CALENDAR_TOKEN_SCOPE = 'calendar:read'

export type SubscriptionUrlProps = {
  environmentId: string
  workspaceId: string
  /** Only the events the token's owner is on call for. */
  onCallOnly: boolean
  /** The personal access token, as handed out at creation. */
  token: string
}

/**
 * The ics feed URL a calendar app subscribes to.
 *
 * Calendar apps fetch the feed themselves and cannot be told to send an Authorization header, so the
 * token travels in the query string (`nwpat`, which SMEP accepts in place of the header). That makes
 * the URL itself a credential - it is shown once and revoked by revoking the token.
 *
 * What the link *asks for* is one workspace; what the token *permits* is every event calendar its
 * owner can read. The environment is a path segment and the workspace a query filter, so whoever
 * holds the link can drop the filter or point it at another environment and get the wider calendar.
 * Narrowing that would take a workspace-bound check in SMEP, not a change here.
 */
export const buildSubscriptionUrl = ({ environmentId, workspaceId, onCallOnly, token }: SubscriptionUrlProps) => {
  const url = new URL(
    `https://smep.${import.meta.env.VITE_NW_API_ROOT_DOMAIN}/environments/${environmentId}/eventCalendar/ics`,
  )
  url.searchParams.set('workspaceIds', workspaceId)
  if (onCallOnly) {
    url.searchParams.set('onCallOnly', 'true')
  }
  url.searchParams.set('nwpat', token)

  return url.toString()
}

/**
 * Google Calendar's "Other calendars > From URL" dialog. It takes no parameters, so the link cannot be
 * handed to it - the caller copies it and the user pastes.
 *
 * The alternative is the `?cid=<encoded url>` deep link, which subscribes in one click. It answered
 * with "failed, check your URL" while SMEP was still refusing HEAD with a 405 and serving the feed as
 * an attachment, which is what a feed validator rejects - so that link is worth trying again once the
 * fix for those is deployed, rather than being written off as Google mis-parsing the query string.
 */
export const GOOGLE_CALENDAR_ADD_BY_URL = 'https://calendar.google.com/calendar/u/0/r/settings/addbyurl'

/** Hands the feed to whichever calendar app the OS has registered, e.g. Apple Calendar or Outlook. */
export const buildWebcalUrl = (subscriptionUrl: string) => subscriptionUrl.replace(/^https:/, 'webcal:')
